import React, { useState } from 'react';
import {
  Card, Button, DatePicker, Space, Alert, Descriptions, Statistic, Row, Col, Typography, message,
  theme, Grid, Divider,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import moment from 'moment';
import { estimacionIvaService, fiscalDataService } from '../../services/api';
import HistoricoDescargasPanel from './HistoricoDescargasPanel';
import ConciliacionIvaPanel from './ConciliacionIvaPanel';
import IvaAcumuladoPanel from './IvaAcumuladoPanel';

const { Text } = Typography;

const pesos = (v) =>
  `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Estimación ISR/IVA de la empresa seleccionada.
 *
 * Usa el mismo endpoint que ve el dueño: el backend resuelve el acceso por
 * actor, así que el cálculo es idéntico por construcción, no por equivalencia.
 *
 * Depende de la descarga masiva del SAT, que puede no estar lista para el rango
 * pedido. Ese estado se muestra tal cual en vez de fingir un resultado.
 */
const EstimationPanel = ({ empresa, embebido = true }) => {
  const screens = Grid.useBreakpoint();
  const esMovil = !screens.md;
  const { token } = theme.useToken();
  const [rango, setRango] = useState([moment().startOf('month'), moment().endOf('month')]);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [acumulado, setAcumulado] = useState(null);

  /**
   * El IVA del periodo aplicado contra el acumulado de la conciliación.
   *
   * Va con el MISMO botón que la estimación, y no en su propio efecto al abrir la
   * ficha: consulta documentos a SW, así que cargarlo solo por entrar cobraría esa
   * consulta en cada visita.
   *
   * Si falla, se calla. Es un bloque adicional sobre la estimación de siempre; que
   * el endpoint nuevo no responda no puede impedir ver la de siempre.
   */
  const traerAcumulado = async () => {
    try {
      setAcumulado(await estimacionIvaService.get(empresa.id, rango?.[0]?.format('YYYY-MM')));
    } catch (err) {
      setAcumulado(err?.response?.data?.ok === false ? err.response.data : null);
    }
  };

  const calcular = async () => {
    setCargando(true);
    setDatos(null);
    setAviso(null);
    setAcumulado(null);

    try {
      const respuesta = await fiscalDataService.getTaxEstimation(
        empresa.id,
        rango?.[0]?.format('YYYY-MM-DD'),
        rango?.[1]?.format('YYYY-MM-DD')
      );
      setDatos(respuesta?.data || respuesta);
      await traerAcumulado();
    } catch (err) {
      const cuerpo = err?.response?.data;

      // 428 / 409: la descarga masiva del SAT no está lista para ese rango.
      // No es un error del usuario ni un fallo de permisos.
      if (cuerpo?.sync_required) {
        setAviso({
          tipo: cuerpo.sync_state === 'in_progress' ? 'info' : 'warning',
          mensaje: cuerpo.error,
        });
      } else {
        message.error(cuerpo?.error || 'No se pudo calcular la estimación');
      }
    } finally {
      setCargando(false);
    }
  };

  const est = datos?.estimation;

  return (
    <Card
      title={embebido ? null : 'Estimación de impuestos'}
      variant={embebido ? 'borderless' : 'outlined'}
      styles={{ body: { padding: embebido ? 0 : undefined } }}
      // En móvil los controles no caben en línea con el título: van al cuerpo,
      // a ancho completo.
      extra={
        esMovil ? null : (
          <Space>
            <DatePicker
              picker="month"
              value={rango?.[0] || null}
              onChange={(m) =>
                setRango(m ? [m.clone().startOf('month'), m.clone().endOf('month')] : [])
              }
              format="MM-YYYY"
              allowClear={false}
            />
            <Button type="primary" icon={<CalculatorOutlined />} loading={cargando} onClick={calcular}>
              Calcular
            </Button>
          </Space>
        )
      }
    >
      {esMovil && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="small">
          <DatePicker
            picker="month"
            value={rango?.[0] || null}
            onChange={(m) =>
              setRango(m ? [m.clone().startOf('month'), m.clone().endOf('month')] : [])
            }
            format="MM-YYYY"
            allowClear={false}
            style={{ width: '100%' }}
          />
          <Button block type="primary" icon={<CalculatorOutlined />} loading={cargando} onClick={calcular}>
            Calcular
          </Button>
        </Space>
      )}
      {empresa?.fiscal_regimen !== '626' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="La estimación solo aplica al régimen RESICO (626)"
          description={`Esta empresa tiene régimen ${empresa?.fiscal_regimen || 'sin definir'}.`}
        />
      )}

      {aviso && (
        <Alert
          type={aviso.tipo}
          showIcon
          style={{ marginBottom: 16 }}
          message={aviso.mensaje}
          description="Los datos provienen de la descarga masiva del SAT. Vuelve a intentar cuando esté disponible."
        />
      )}

      {!est && !aviso && (
        <Text type="secondary">Selecciona un periodo y calcula para ver la estimación.</Text>
      )}

      {est && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <Statistic title="Ingresos del periodo" value={pesos(est.ingresos)} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic title="ISR a pagar" value={pesos(est.isr_a_pagar)} valueStyle={{ color: token.colorError }} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={est.iva_a_favor > 0 ? 'IVA a favor' : 'IVA a pagar'}
                value={pesos(est.iva_a_favor > 0 ? est.iva_a_favor : est.iva_a_pagar)}
                valueStyle={{ color: est.iva_a_favor > 0 ? token.colorSuccess : token.colorError }}
              />
            </Col>
          </Row>

          <Descriptions bordered={!esMovil} size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Tasa ISR aplicada">
              {(Number(est.tasa_isr || 0) * 100).toFixed(2)}%
            </Descriptions.Item>
            <Descriptions.Item label="ISR calculado">{pesos(est.isr_calculado)}</Descriptions.Item>
            <Descriptions.Item label="ISR retenido">{pesos(est.isr_retenido)}</Descriptions.Item>
            <Descriptions.Item label="IVA trasladado">{pesos(est.iva_trasladado)}</Descriptions.Item>
            <Descriptions.Item label="IVA retenido">{pesos(est.iva_retenido)}</Descriptions.Item>
            <Descriptions.Item label="IVA acreditable">{pesos(est.iva_acreditable)}</Descriptions.Item>
          </Descriptions>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Estimación informativa calculada sobre los CFDI descargados del SAT para el periodo.
            No sustituye la declaración.
          </Text>
        </Space>
      )}

      {/* EL IVA CON ARRASTRE va aquí, pegado a la estimación del periodo y antes del
          histórico: es la misma pregunta del bloque de arriba —cuánto se paga— pero
          contando el saldo a favor que viene de atrás, y separarlas obligaría a
          comparar dos pantallas para entender por qué una dice 0 y la otra no.
          Ver change conciliacion-iva-cadena-propia. */}
      {!!acumulado && (
        <>
          <Divider orientation="left" style={{ marginTop: 24 }}>
            IVA con saldo a favor acumulado
          </Divider>
          <IvaAcumuladoPanel datos={acumulado} />
        </>
      )}

      {/* HISTÓRICO DE DOS AÑOS. Vive aquí y no en una pestaña propia: es una acción
          ocasional, y una pestaña más en una ficha que ya tiene varias se paga en
          todas las visitas, no solo en las que se usa. Además es donde ya está quien
          hace la conciliación de IVA. Ver change historico-dos-anios-cartera. */}
      <Divider orientation="left" style={{ marginTop: 24 }}>
        Histórico para conciliación
      </Divider>
      <HistoricoDescargasPanel empresa={empresa} />

      {/* La conciliación va inmediatamente después del histórico porque depende de
          él: no calcula nada hasta que los 24 meses estén descargados, y cuando
          falta algo señala hacia arriba. Separarlos obligaría a ir y volver. */}
      <Divider orientation="left" style={{ marginTop: 24 }}>
        Conciliación de IVA
      </Divider>
      <ConciliacionIvaPanel empresa={empresa} />
    </Card>
  );
};

export default EstimationPanel;
